<template>
  <div class="chat-color-palette">
    <div
      class="chat-color-palette__grid"
      role="listbox"
      :aria-label="t('onboarding.wizard.data-entry.brand-colors')"
    >
      <button
        v-for="hex in palette"
        :key="hex"
        type="button"
        role="option"
        class="chat-color-palette__option"
        :class="{ 'chat-color-palette__option--selected': selectedHex === hex }"
        :aria-selected="selectedHex === hex"
        :disabled="busy"
        @click="selectHex(hex)"
      >
        <span
          class="chat-color-palette__swatch"
          :style="{ backgroundColor: hex }"
        >
          <v-icon
            v-if="selectedHex === hex"
            size="18"
            :color="hasSufficientContrast(hex) ? 'white' : 'black'"
          >
            mdi-check
          </v-icon>
        </span>
        <span class="chat-color-palette__hex">{{ hex.toUpperCase() }}</span>
      </button>
    </div>
    <ColorContrastStatus
      v-if="selectedHex"
      class="mt-1"
      :color="selectedHex"
      :sufficient="hasSufficientContrast(selectedHex)"
    />
    <button
      v-if="allowCustom"
      type="button"
      class="chat-color-palette__none text-caption font-weight-bold text-primary"
      :disabled="busy"
      @click="emit('none-of-above', { formKey })"
    >
      {{ t('onboarding.wizard.chat-widgets.none-of-above') }}
    </button>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useI18n } from 'vue-i18n'

import ColorContrastStatus from '@/components/ColorContrastStatus.vue'
import { meetsAA } from '@/lib/colorContrastRatio.js'

const props = defineProps<{
  palette: string[]
  formKey: string
  allowCustom?: boolean
  busy?: boolean
}>()

const emit = defineEmits<{
  select: [payload: { formKey: string; hex: string }]
  'none-of-above': [payload: { formKey: string }]
}>()

const { t } = useI18n()
const selectedHex = ref<string | null>(null)

const hasSufficientContrast = (hex: string) => meetsAA(hex, '#FFFFFF', 4.6)

const selectHex = (hex: string) => {
  selectedHex.value = hex
  emit('select', { formKey: props.formKey, hex })
}
</script>

<style scoped>
.chat-color-palette {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.chat-color-palette__grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 8px;
}

.chat-color-palette__option {
  display: flex;
  align-items: center;
  gap: 9px;
  width: 100%;
  min-width: 0;
  padding: 8px;
  border: 1px solid rgba(var(--v-border-color), 0.18);
  border-radius: 12px;
  background: rgb(var(--v-theme-surface));
  cursor: pointer;
  text-align: left;
  transition:
    border-color 140ms ease,
    background-color 140ms ease,
    box-shadow 140ms ease,
    transform 140ms ease;
}

.chat-color-palette__option:hover:not(:disabled) {
  border-color: rgba(var(--v-theme-primary), 0.45);
  background: rgba(var(--v-theme-primary), 0.04);
  transform: translateY(-1px);
}

.chat-color-palette__option:focus-visible {
  outline: 2px solid rgb(var(--v-theme-primary));
  outline-offset: 2px;
}

.chat-color-palette__option:disabled {
  cursor: default;
  opacity: 0.62;
}

.chat-color-palette__option--selected {
  border-color: rgb(var(--v-theme-primary));
  background: rgba(var(--v-theme-primary), 0.07);
  box-shadow: 0 0 0 1px rgb(var(--v-theme-primary));
}

.chat-color-palette__swatch {
  display: grid;
  width: 34px;
  height: 34px;
  place-items: center;
  border-radius: 9px;
  flex-shrink: 0;
  border: 1px solid rgba(0, 0, 0, 0.12);
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.16);
}

.chat-color-palette__hex {
  overflow: hidden;
  font-size: 0.75rem;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.chat-color-palette__none {
  align-self: flex-start;
  min-height: 32px;
  margin-top: 2px;
  padding: 4px 2px;
  background: none;
  border: none;
  cursor: pointer;
  letter-spacing: 0.04em;
}

@media (max-width: 360px) {
  .chat-color-palette__grid {
    grid-template-columns: 1fr;
  }
}
</style>
